/**
 * Simple Auth Client for Frontend
 * All auth handled by backend, frontend just manages JWT cookie
 */

import { getTrackingData } from './browserDetection';
import { getFacebookCookies, trackFacebookEvent } from './facebook';
import { fingerprintService } from './fingerprint';

export interface User {
  id: string;
  email: string;
  displayName: string;
  credits: number;
  isAdmin: boolean;
  emailVerified: boolean;
  createdAt: string;
  restrictedFeatures?: string[];
  restrictionReason?: string;
  /** Whether the account has completed TOTP 2FA enrollment. Drives account settings. */
  totpEnabled?: boolean;
  /** Connected accounts / sign-in methods (account settings). */
  googleConnected?: boolean;
  authProvider?: 'email' | 'google' | string;
  hasPassword?: boolean;
  avatarUrl?: string | null;
}

export interface AuthResponse {
  success: boolean;
  token?: string;
  user?: User;
  error?: string;
  // 2FA gate: the password step returns one of these instead of a session token.
  // `requires2FA` means the account (admin OR opted-in consumer) is enrolled and
  // must POST a 6-digit code to /auth/2fa/verify; `requires2FASetup` means an admin
  // must enroll first (forced admin enrollment, only supported in the admin dashboard).
  requires2FA?: boolean;
  challengeToken?: string;
  requires2FASetup?: boolean;
}

export interface MeResponse {
  success: boolean;
  user?: User;
  token?: string;
  error?: string;
}

/**
 * Sign up new user
 */
export async function signup(email: string, displayName: string, password: string): Promise<AuthResponse> {
  // Collect browser and location tracking data
  const { browserInfo, locationInfo } = await getTrackingData();

  // Get browser fingerprint for ban prevention
  const browserFingerprint = await fingerprintService.getFingerprint();

  // Track signup with Facebook Pixel (frontend)
  const { fbc, fbp } = getFacebookCookies();
  const eventId = trackFacebookEvent('CompleteRegistration');

  // Pull referral code from localStorage (set by /r/[code] landing page).
  // Cleared on successful signup so a refresh won't reattach an old code.
  let referralCode: string | undefined;
  if (typeof window !== 'undefined') {
    try {
      referralCode = localStorage.getItem('referralCode') || undefined;
    } catch {}
  }

  const response = await fetch('/api/auth/signup', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email,
      displayName,
      password,
      browserInfo,
      locationInfo,
      browserFingerprint, // For ban prevention
      referralCode, // Referral program — undefined if user didn't visit /r/...
      // Facebook tracking for backend Conversion API
      fbc,
      fbp,
      eventId,
    }),
    credentials: 'include', // Important: include cookies
  });

  const data = await response.json();
  // Clear the consumed code so it doesn't follow the user around forever.
  if (typeof window !== 'undefined' && data?.success) {
    try {
      localStorage.removeItem('referralCode');
    } catch {}
  }
  return data;
}

/**
 * Login existing user
 */
export async function login(email: string, password: string): Promise<AuthResponse> {
  // Collect browser and location tracking data
  const { browserInfo, locationInfo } = await getTrackingData();
  
  // Get browser fingerprint for ban prevention
  const browserFingerprint = await fingerprintService.getFingerprint();
  
  const response = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ 
      email, 
      password,
      browserInfo,
      locationInfo,
      browserFingerprint, // For ban prevention
    }),
    credentials: 'include',
  });

  return response.json();
}

/**
 * Sign in with Google. `credential` is the ID token from the Google button.
 * Proxies to /api/auth/google-callback, which verifies it server-side and sets
 * the auth cookie. Returns the same shape as login() (incl. the 2FA gate).
 */
export async function googleLogin(credential: string): Promise<AuthResponse> {
  const { browserInfo, locationInfo } = await getTrackingData();
  const browserFingerprint = await fingerprintService.getFingerprint();

  // Attach a referral code the same way signup does (set by /r/[code]).
  let referralCode: string | undefined;
  if (typeof window !== 'undefined') {
    try {
      referralCode = localStorage.getItem('referralCode') || undefined;
    } catch {}
  }

  const response = await fetch('/api/auth/google-callback', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      credential,
      browserInfo,
      locationInfo,
      browserFingerprint,
      referralCode,
    }),
    credentials: 'include',
  });

  const data = await response.json();
  // Clear a consumed referral code so it doesn't follow the user around.
  if (typeof window !== 'undefined' && data?.success && data?.user) {
    try {
      localStorage.removeItem('referralCode');
    } catch {}
  }
  return data;
}

/**
 * Complete a 2FA challenge (admin or opted-in consumer).
 * Called after login() returns { requires2FA, challengeToken }. On success the
 * proxy route sets the auth cookie and returns { success, token, user }.
 */
export async function verify2FA(challengeToken: string, code: string): Promise<AuthResponse> {
  const { browserInfo, locationInfo } = await getTrackingData();

  const response = await fetch('/api/auth/2fa/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      challengeToken,
      code,
      browserInfo,
      locationInfo,
    }),
    credentials: 'include',
  });

  return response.json();
}

/**
 * Logout current user
 */
export async function logout(): Promise<void> {
  await fetch('/api/auth/logout', {
    method: 'POST',
    credentials: 'include',
  });
}

/**
 * Get current user from backend
 */
export async function getCurrentUser(): Promise<User | null> {
  try {
    const response = await fetch('/api/auth/me', {
      credentials: 'include',
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    return data.user || null;
  } catch (error) {
    console.error('Failed to get current user:', error);
    return null;
  }
}

/**
 * Get current user with token for Socket.IO
 */
export async function getCurrentUserWithToken(): Promise<MeResponse> {
  try {
    const response = await fetch('/api/auth/me', {
      credentials: 'include',
    });

    if (!response.ok) {
      return { success: false, error: 'Not authenticated' };
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Failed to get current user:', error);
    return { success: false, error: 'Failed to fetch user' };
  }
}


